'use client';

/**
 * AnimatedElasticSlider — Morfi's elastic-slider primitive.
 *
 * Implementation transcribed from the chanhdai elastic-slider reference at
 * https://chanhdai.com/components/elastic-slider (registry payload at
 * https://chanhdai.com/r/elastic-slider.json), with all colors and dimensions
 * replaced by Morfi semantic tokens. The drag thresholds, spring physics,
 * magnetic-snap behaviour, and keyboard model are preserved verbatim — the
 * tactile feel is the upstream contribution; Morfi owns the visual identity
 * via Tailwind utilities mapped to design tokens.
 *
 * The dependent `useControllableState` hook from the chanhdai registry is
 * inlined into this file (its single use site).
 *
 * This file is NOT part of the shadcn registry and is NOT regenerated by
 * `pnpm refresh:shadcn-stock`. No `.shadcn-stock/animated-elastic-slider.tsx`
 * snapshot exists; FQ-111 stays silent. See spec 079
 * (frontend/specs/079-creator-rewards-panel) and the apple-switch precedent
 * in spec 054.
 */
import { c as _c } from "react/compiler-runtime";
import { animate, domAnimation, LazyMotion, type MotionValue, m, useMotionValue, useReducedMotion, useTransform } from 'motion/react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { type Dispatch, type SetStateAction, useEffect, useInsertionEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/ui/utils';

// ───────────────────────────────────────────────────────────────────────────
// Tunable constants — preserved verbatim from chanhdai upstream
// ───────────────────────────────────────────────────────────────────────────

const CLICK_THRESHOLD: number = 3;
const DEAD_ZONE: number = 32;
const MAX_CURSOR_RANGE: number = 200;
const MAX_STRETCH: number = 8;
const HANDLE_BUFFER: number = 8;
const LABEL_OFFSET: number = 12 + 4;
const VALUE_OFFSET: number = 12 - 8;

// ───────────────────────────────────────────────────────────────────────────
// Inlined `useControllableState` (from chanhdai registry — single use site).
// Originally adapted from radix-ui/primitives.
// ───────────────────────────────────────────────────────────────────────────

type ChangeHandler<T> = (state: T) => void;
interface UseControllableStateParams<T> {
  prop?: T | undefined;
  defaultProp: T;
  onChange?: ChangeHandler<T>;
}
function useControllableState(t0) {
  const $ = _c(11);
  const {
    prop,
    defaultProp,
    onChange: t1
  } = t0;
  const onChange = t1 === undefined ? _temp : t1;
  let t2;
  if ($[0] !== defaultProp || $[1] !== onChange) {
    t2 = {
      defaultProp,
      onChange
    };
    $[0] = defaultProp;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [uncontrolledProp, setUncontrolledProp, onChangeRef] = useUncontrolledState(t2);
  const isControlled = prop !== undefined;
  const value = isControlled ? prop : uncontrolledProp;
  let t3;
  if ($[3] !== isControlled || $[4] !== onChangeRef || $[5] !== prop || $[6] !== setUncontrolledProp) {
    t3 = nextValue => {
      if (isControlled) {
        const resolved = typeof nextValue === "function" ? (nextValue as (prev: T) => T)(prop) : nextValue;
        if (resolved !== prop) {
          onChangeRef.current?.(resolved);
        }
      } else {
        setUncontrolledProp(nextValue);
      }
    };
    $[3] = isControlled;
    $[4] = onChangeRef;
    $[5] = prop;
    $[6] = setUncontrolledProp;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const setValue = t3;
  let t4;
  if ($[8] !== setValue || $[9] !== value) {
    t4 = [value, setValue];
    $[8] = setValue;
    $[9] = value;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function _temp() {}
function useUncontrolledState(t0) {
  const $ = _c(8);
  const {
    defaultProp,
    onChange
  } = t0;
  const [value, setValue] = useState(defaultProp);
  const prevValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  let t1;
  let t2;
  if ($[0] !== onChange) {
    t1 = () => {
      onChangeRef.current = onChange;
    };
    t2 = [onChange];
    $[0] = onChange;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useInsertionEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== value) {
    t3 = () => {
      if (prevValueRef.current !== value) {
        onChangeRef.current?.(value);
        prevValueRef.current = value;
      }
    };
    t4 = [value];
    $[3] = value;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== value) {
    t5 = [value, setValue, onChangeRef];
    $[6] = value;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

// ───────────────────────────────────────────────────────────────────────────
// Math helpers
// ───────────────────────────────────────────────────────────────────────────

function clampValue(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function decimalsForStep(step: number): number {
  const s = step.toString();
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
}
function roundValue(val: number, step: number): number {
  const snapped = Math.round(val / step) * step;
  const d = decimalsForStep(step);
  if (d === 0) return Math.round(snapped);
  const factor = 10 ** d;
  return Math.round(snapped * factor) / factor;
}

// Magnetic snap to the nearest decile when within 3.125% of it.
function snapToDecile(rawValue: number, min: number, max: number): number {
  const normalized = (rawValue - min) / (max - min);
  const nearest = Math.round(normalized * 10) / 10;
  if (Math.abs(normalized - nearest) <= 0.03125) {
    return min + nearest * (max - min);
  }
  return rawValue;
}

// ───────────────────────────────────────────────────────────────────────────
// Public surface
// ───────────────────────────────────────────────────────────────────────────

export interface AnimatedElasticSliderProps {
  /** Visible label rendered inside the track on the left. */
  label: string;
  /** Controlled current value. */
  value: number;
  /** Called on every value change (drag, click, keyboard step). */
  onValueChange: (next: number) => void;
  /** Minimum slider value. */
  min: number;
  /** Maximum slider value. */
  max: number;
  /** Step granularity. Decile-magnetic snap when (max - min) / step > 10. */
  step: number;
  /** Format the displayed value (right-aligned inside track + `aria-valuetext`). */
  formatValue: (value: number) => string;
  /** Optional layout-only className passthrough (no token overrides). */
  className?: string;
  /** Optional ARIA label override. Defaults to `label`. */
  'aria-label'?: string;
}
interface ElasticSliderModel {
  value: number;
  displayValue: string;
  isActive: boolean;
  keyboardFocusRing: boolean;
  shouldReduceMotion: boolean | null;
  fillWidth: MotionValue<string>;
  handleLeft: MotionValue<string>;
  rubberWidth: MotionValue<string>;
  rubberX: MotionValue<number>;
  handleOpacity: number;
  valueDodge: boolean;
  hashMarkCount: number;
  hashMarkPct: (i: number) => number;
  setIsHovered: Dispatch<SetStateAction<boolean>>;
  handlePointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  handlePointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  handlePointerUp: (e: ReactPointerEvent<HTMLDivElement>) => void;
  handleKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
  handleTrackFocus: () => void;
  handleTrackBlur: () => void;
}

/**
 * All of the elastic-slider's controllable value, drag/keyboard/hover state,
 * spring physics (motion values + animations), magnetic-snap math, and the
 * label/value dodge measurement. Kept as a hook in this file so the component
 * body stays a thin renderer. The four DOM refs are OWNED by the component (used
 * as `ref={}`) and threaded in here — routing a ref through the returned model
 * would trip the React Compiler refs rule, which is not file-exempt here.
 */
function useElasticSlider(t0) {
  const $ = _c(84);
  const {
    value: valueProp,
    onValueChange,
    min,
    max,
    step,
    formatValue,
    wrapperRef,
    trackRef,
    labelRef,
    valueRef
  } = t0;
  let t1;
  if ($[0] !== onValueChange || $[1] !== valueProp) {
    t1 = {
      prop: valueProp,
      defaultProp: valueProp,
      onChange: onValueChange
    };
    $[0] = onValueChange;
    $[1] = valueProp;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [t2, setValue] = useControllableState(t1);
  const value = t2 === undefined ? min : t2;
  const shouldReduceMotion = useReducedMotion();
  const [isInteracting, setIsInteracting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [keyboardFocusRing, setKeyboardFocusRing] = useState(false);
  const pointerDownPos = useRef(null);
  const pendingPointerFocusRef = useRef(false);
  const isClickRef = useRef(true);
  const animRef = useRef(null);
  const wrapperRectRef = useRef(null);
  const scaleRef = useRef(1);
  const percentage = (value - min) / (max - min) * 100;
  const isActive = isInteracting || isHovered;
  let t3;
  if ($[3] !== formatValue || $[4] !== value) {
    t3 = formatValue(value);
    $[3] = formatValue;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const displayValue = t3;
  const fillPercent = useMotionValue(percentage);
  const fillWidth = useTransform(fillPercent, _temp2);
  const handleLeft = useTransform(fillPercent, _temp3);
  const rubberStretch = useMotionValue(0);
  const rubberWidth = useTransform(rubberStretch, _temp4);
  const rubberX = useTransform(rubberStretch, _temp5);
  let t4;
  let t5;
  if ($[6] !== fillPercent || $[7] !== isInteracting || $[8] !== percentage) {
    t4 = () => {
      if (!isInteracting && !animRef.current) {
        fillPercent.jump(percentage);
      }
    };
    t5 = [percentage, isInteracting, fillPercent];
    $[6] = fillPercent;
    $[7] = isInteracting;
    $[8] = percentage;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== max || $[12] !== min || $[13] !== wrapperRef) {
    t6 = clientX => {
      const rect = wrapperRectRef.current;
      if (rect === null) {
        return min;
      }
      const sceneX = (clientX - rect.left) / scaleRef.current;
      const nativeWidth = wrapperRef.current?.offsetWidth ?? rect.width;
      const percent = clampValue(sceneX / nativeWidth, 0, 1);
      return clampValue(min + percent * (max - min), min, max);
    };
    $[11] = max;
    $[12] = min;
    $[13] = wrapperRef;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const positionToValue = t6;
  let t7;
  if ($[15] !== max || $[16] !== min) {
    t7 = v => (v - min) / (max - min) * 100;
    $[15] = max;
    $[16] = min;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const percentFromValue = t7;
  let t8;
  if ($[18] !== fillPercent || $[19] !== shouldReduceMotion) {
    t8 = targetPercent => {
      animRef.current?.stop();
      if (shouldReduceMotion === true) {
        fillPercent.jump(targetPercent);
        animRef.current = null;
        return;
      }
      animRef.current = animate(fillPercent, targetPercent, {
        type: "spring",
        stiffness: 300,
        damping: 25,
        mass: 0.8,
        onComplete: () => {
          animRef.current = null;
        }
      });
    };
    $[18] = fillPercent;
    $[19] = shouldReduceMotion;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const animateFillTo = t8;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (clientX_0, sign) => {
      const rect_0 = wrapperRectRef.current;
      if (rect_0 === null) {
        return 0;
      }
      const distancePast = sign < 0 ? rect_0.left - clientX_0 : clientX_0 - rect_0.right;
      const overflow = Math.max(0, distancePast - DEAD_ZONE);
      return sign * MAX_STRETCH * Math.sqrt(Math.min(overflow / MAX_CURSOR_RANGE, 1));
    };
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const computeRubberStretch = t9;
  let t10;
  if ($[22] !== trackRef || $[23] !== wrapperRef) {
    t10 = e => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      pointerDownPos.current = {
        x: e.clientX,
        y: e.clientY
      };
      isClickRef.current = true;
      setIsInteracting(true);
      pendingPointerFocusRef.current = true;
      setKeyboardFocusRing(false);
      trackRef.current?.focus({
        preventScroll: true
      });
      requestAnimationFrame(() => {
        pendingPointerFocusRef.current = false;
      });
      const wrapper = wrapperRef.current;
      if (wrapper !== null) {
        const rect_1 = wrapper.getBoundingClientRect();
        wrapperRectRef.current = rect_1;
        scaleRef.current = rect_1.width / wrapper.offsetWidth;
      }
    };
    $[22] = trackRef;
    $[23] = wrapperRef;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handlePointerDown = t10;
  let t11;
  if ($[25] !== fillPercent || $[26] !== isInteracting || $[27] !== percentFromValue || $[28] !== positionToValue || $[29] !== rubberStretch || $[30] !== setValue || $[31] !== shouldReduceMotion || $[32] !== step) {
    t11 = e_0 => {
      if (!isInteracting || pointerDownPos.current === null) {
        return;
      }
      const dx = e_0.clientX - pointerDownPos.current.x;
      const dy = e_0.clientY - pointerDownPos.current.y;
      if (isClickRef.current && Math.hypot(dx, dy) > CLICK_THRESHOLD) {
        isClickRef.current = false;
        setIsDragging(true);
      }
      if (isClickRef.current) {
        return;
      }
      const rect_2 = wrapperRectRef.current;
      if (rect_2 !== null && shouldReduceMotion !== true) {
        if (e_0.clientX < rect_2.left) {
          rubberStretch.jump(computeRubberStretch(e_0.clientX, -1));
        } else {
          if (e_0.clientX > rect_2.right) {
            rubberStretch.jump(computeRubberStretch(e_0.clientX, 1));
          } else {
            rubberStretch.jump(0);
          }
        }
      }
      const newValue = positionToValue(e_0.clientX);
      animRef.current?.stop();
      animRef.current = null;
      fillPercent.jump(percentFromValue(newValue));
      setValue(roundValue(newValue, step));
    };
    $[25] = fillPercent;
    $[26] = isInteracting;
    $[27] = percentFromValue;
    $[28] = positionToValue;
    $[29] = rubberStretch;
    $[30] = setValue;
    $[31] = shouldReduceMotion;
    $[32] = step;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const handlePointerMove = t11;
  let t12;
  if ($[34] !== animateFillTo || $[35] !== isInteracting || $[36] !== max || $[37] !== min || $[38] !== percentFromValue || $[39] !== positionToValue || $[40] !== rubberStretch || $[41] !== setValue || $[42] !== shouldReduceMotion || $[43] !== step) {
    t12 = e_1 => {
      if (!isInteracting) {
        return;
      }
      if (isClickRef.current) {
        const rawValue = positionToValue(e_1.clientX);
        const discreteSteps = (max - min) / step;
        const snapped = discreteSteps <= 10 ? clampValue(min + Math.round((rawValue - min) / step) * step, min, max) : snapToDecile(rawValue, min, max);
        animateFillTo(percentFromValue(snapped));
        setValue(roundValue(snapped, step));
      }
      if (shouldReduceMotion !== true && rubberStretch.get() !== 0) {
        animate(rubberStretch, 0, {
          type: "spring",
          visualDuration: 0.35,
          bounce: 0.15
        });
      }
      setIsInteracting(false);
      setIsDragging(false);
      pointerDownPos.current = null;
    };
    $[34] = animateFillTo;
    $[35] = isInteracting;
    $[36] = max;
    $[37] = min;
    $[38] = percentFromValue;
    $[39] = positionToValue;
    $[40] = rubberStretch;
    $[41] = setValue;
    $[42] = shouldReduceMotion;
    $[43] = step;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const handlePointerUp = t12;
  let t13;
  if ($[45] !== animateFillTo || $[46] !== max || $[47] !== min || $[48] !== percentFromValue || $[49] !== setValue || $[50] !== step || $[51] !== value) {
    t13 = e_2 => {
      const arrowStep = e_2.shiftKey ? step * 10 : step;
      let next;
      bb132: switch (e_2.key) {
        case "ArrowRight":
        case "ArrowUp":
          {
            next = value + arrowStep;
            break bb132;
          }
        case "ArrowLeft":
        case "ArrowDown":
          {
            next = value - arrowStep;
            break bb132;
          }
        case "Home":
          {
            next = min;
            break bb132;
          }
        case "End":
          {
            next = max;
            break bb132;
          }
        default:
          {
            return;
          }
      }
      e_2.preventDefault();
      setKeyboardFocusRing(true);
      const snapped_0 = roundValue(clampValue(next, min, max), step);
      animateFillTo(percentFromValue(snapped_0));
      setValue(snapped_0);
    };
    $[45] = animateFillTo;
    $[46] = max;
    $[47] = min;
    $[48] = percentFromValue;
    $[49] = setValue;
    $[50] = step;
    $[51] = value;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  const handleKeyDown = t13;
  let t14;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      if (!pendingPointerFocusRef.current) {
        setKeyboardFocusRing(true);
      }
    };
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  const handleTrackFocus = t14;
  let t15;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setKeyboardFocusRing(false);
    };
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  const handleTrackBlur = t15;
  let t16;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      left: 38,
      right: 72
    };
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const [dodge, setDodge] = useState(t16);
  let t17;
  let t18;
  if ($[56] !== labelRef || $[57] !== valueRef || $[58] !== wrapperRef) {
    t17 = () => {
      const wrapper_0 = wrapperRef.current;
      if (wrapper_0 === null) {
        return;
      }
      const measure = () => {
        const trackWidth = wrapper_0.offsetWidth;
        if (trackWidth <= 0) {
          return;
        }
        const labelEl = labelRef.current;
        const valueEl = valueRef.current;
        const left = labelEl !== null ? (LABEL_OFFSET + labelEl.offsetWidth + HANDLE_BUFFER) / trackWidth * 100 : 38;
        const right = valueEl !== null ? (trackWidth - VALUE_OFFSET - valueEl.offsetWidth - HANDLE_BUFFER) / trackWidth * 100 : 72;
        setDodge(prev => prev.left === left && prev.right === right ? prev : {
          left,
          right
        });
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(wrapper_0);
      if (labelRef.current !== null) {
        observer.observe(labelRef.current);
      }
      if (valueRef.current !== null) {
        observer.observe(valueRef.current);
      }
      return () => observer.disconnect();
    };
    t18 = [wrapperRef, labelRef, valueRef];
    $[56] = labelRef;
    $[57] = valueRef;
    $[58] = wrapperRef;
    $[59] = t17;
    $[60] = t18;
  } else {
    t17 = $[59];
    t18 = $[60];
  }
  useLayoutEffect(t17, t18);
  const valueDodge = percentage < dodge.left || percentage > dodge.right;
  const handleOpacity = valueDodge ? 0.1 : isDragging ? 0.75 : 0.5;
  const discreteSteps_0 = (max - min) / step;
  const hashMarkCount = discreteSteps_0 <= 10 ? Math.max(0, discreteSteps_0 - 1) : 9;
  let t19;
  if ($[61] !== discreteSteps_0 || $[62] !== max || $[63] !== min || $[64] !== step) {
    t19 = i => discreteSteps_0 <= 10 ? (i + 1) * step / (max - min) * 100 : (i + 1) * 10;
    $[61] = discreteSteps_0;
    $[62] = max;
    $[63] = min;
    $[64] = step;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  const hashMarkPct = t19;
  let t20;
  if ($[66] !== displayValue || $[67] !== fillWidth || $[68] !== handleKeyDown || $[69] !== handleLeft || $[70] !== handleOpacity || $[71] !== handlePointerDown || $[72] !== handlePointerMove || $[73] !== handlePointerUp || $[74] !== hashMarkCount || $[75] !== hashMarkPct || $[76] !== isActive || $[77] !== keyboardFocusRing || $[78] !== rubberWidth || $[79] !== rubberX || $[80] !== shouldReduceMotion || $[81] !== value || $[82] !== valueDodge) {
    t20 = {
      value,
      displayValue,
      isActive,
      keyboardFocusRing,
      shouldReduceMotion,
      fillWidth,
      handleLeft,
      rubberWidth,
      rubberX,
      handleOpacity,
      valueDodge,
      hashMarkCount,
      hashMarkPct,
      setIsHovered,
      handlePointerDown,
      handlePointerMove,
      handlePointerUp,
      handleKeyDown,
      handleTrackFocus,
      handleTrackBlur
    };
    $[66] = displayValue;
    $[67] = fillWidth;
    $[68] = handleKeyDown;
    $[69] = handleLeft;
    $[70] = handleOpacity;
    $[71] = handlePointerDown;
    $[72] = handlePointerMove;
    $[73] = handlePointerUp;
    $[74] = hashMarkCount;
    $[75] = hashMarkPct;
    $[76] = isActive;
    $[77] = keyboardFocusRing;
    $[78] = rubberWidth;
    $[79] = rubberX;
    $[80] = shouldReduceMotion;
    $[81] = value;
    $[82] = valueDodge;
    $[83] = t20;
  } else {
    t20 = $[83];
  }
  return t20;
}
function _temp5(s_0) {
  return s_0 < 0 ? s_0 : 0;
}
function _temp4(s) {
  return `calc(100% + ${Math.abs(s)}px)`;
}
function _temp3(pct_0) {
  return `max(4px, calc(${pct_0}% - 8px))`;
}
function _temp2(pct) {
  return `${pct}%`;
}
export function AnimatedElasticSlider(props) {
  const $ = _c(60);
  const {
    label,
    min,
    max,
    className,
    "aria-label": ariaLabel
  } = props;
  const wrapperRef = useRef(null);
  const trackRef = useRef(null);
  const labelRef = useRef(null);
  const valueRef = useRef(null);
  let t0;
  if ($[0] !== props) {
    t0 = {
      ...props,
      wrapperRef,
      trackRef,
      labelRef,
      valueRef
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    value,
    displayValue,
    isActive,
    keyboardFocusRing,
    shouldReduceMotion,
    fillWidth,
    handleLeft,
    rubberWidth,
    rubberX,
    handleOpacity,
    valueDodge,
    hashMarkCount,
    hashMarkPct,
    setIsHovered,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
    handleTrackFocus,
    handleTrackBlur
  } = useElasticSlider(t0);
  let t1;
  if ($[2] !== className) {
    t1 = cn("[--elastic-slider-height:--spacing(8)] [--elastic-slider-radius:var(--radius-control)]", "[--elastic-slider-bg:var(--background-tertiary)]", "[--elastic-slider-fill:color-mix(in_srgb,var(--foreground-primary)_20%,transparent)]", "[--elastic-slider-fill-active:color-mix(in_srgb,var(--foreground-primary)_20%,transparent)]", "[--elastic-slider-hash:transparent]", "[--elastic-slider-handle:var(--foreground-primary)]", "[--elastic-slider-label:var(--foreground-secondary)]", "[--elastic-slider-focus:var(--foreground-primary)]", "relative h-(--elastic-slider-height)", className);
    $[2] = className;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = ariaLabel ?? label;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("group/elastic-slider absolute inset-0 cursor-pointer touch-none overflow-hidden rounded-(--elastic-slider-radius) bg-(--elastic-slider-bg) outline-none select-none", "data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-border-primary/50");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== rubberWidth || $[6] !== rubberX) {
    t4 = {
      width: rubberWidth,
      x: rubberX
    };
    $[5] = rubberWidth;
    $[6] = rubberX;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] !== setIsHovered) {
    t5 = () => setIsHovered(true);
    t6 = () => setIsHovered(false);
    $[8] = setIsHovered;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== hashMarkCount || $[12] !== hashMarkPct) {
    let t8;
    if ($[14] !== hashMarkPct) {
      t8 = (_, i) => <div key={`hash-${String(i)}`} className={cn("absolute top-1/2 h-2 w-px -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-fast", "bg-transparent group-data-[active=true]/elastic-slider:bg-(--elastic-slider-hash)")} style={{
        left: `${hashMarkPct(i)}%`
      }} />;
      $[14] = hashMarkPct;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t7 = <div data-slot="elastic-slider-hash-marks" aria-hidden="true" className="pointer-events-none absolute inset-0">{Array.from({
        length: hashMarkCount
      }, t8)}</div>;
    $[11] = hashMarkCount;
    $[12] = hashMarkPct;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("pointer-events-none absolute inset-y-0 left-0 transition-colors", "bg-(--elastic-slider-fill) group-data-[active=true]/elastic-slider:bg-(--elastic-slider-fill-active)");
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== fillWidth) {
    t9 = <m.div data-slot="elastic-slider-fill" aria-hidden="true" className={t8} style={{
      width: fillWidth
    }} />;
    $[17] = fillWidth;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== handleLeft) {
    t10 = {
      left: handleLeft,
      y: "-50%"
    };
    $[19] = handleLeft;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = valueDodge ? 0.75 : 1;
  let t12;
  if ($[21] !== handleOpacity || $[22] !== t11) {
    t12 = {
      opacity: handleOpacity,
      scaleX: 1,
      scaleY: t11
    };
    $[21] = handleOpacity;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== shouldReduceMotion) {
    t13 = shouldReduceMotion === true ? {
      duration: 0
    } : {
      scaleX: {
        type: "spring",
        visualDuration: 0.25,
        bounce: 0.15
      },
      scaleY: {
        type: "spring",
        visualDuration: 0.2,
        bounce: 0.1
      },
      opacity: {
        duration: 0.15
      }
    };
    $[24] = shouldReduceMotion;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t10 || $[27] !== t12 || $[28] !== t13) {
    t14 = <m.div data-slot="elastic-slider-handle" aria-hidden="true" className="pointer-events-none absolute top-1/2 h-5 w-1 rounded-full bg-(--elastic-slider-handle)" style={t10} animate={t12} transition={t13} />;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== label) {
    t15 = <span ref={labelRef} data-slot="elastic-slider-label" aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 inline-flex -translate-y-1/2 items-center text-body font-medium text-(--elastic-slider-label) transition-colors">{label}</span>;
    $[30] = label;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn("pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-body font-medium tabular-nums transition-colors", "text-foreground-secondary group-data-[active=true]/elastic-slider:text-(--elastic-slider-focus)");
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== displayValue) {
    t17 = <span ref={valueRef} data-slot="elastic-slider-value" aria-hidden="true" className={t16}>{displayValue}</span>;
    $[33] = displayValue;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== displayValue || $[36] !== handleKeyDown || $[37] !== handlePointerDown || $[38] !== handlePointerMove || $[39] !== handlePointerUp || $[40] !== handleTrackBlur || $[41] !== handleTrackFocus || $[42] !== isActive || $[43] !== keyboardFocusRing || $[44] !== max || $[45] !== min || $[46] !== t14 || $[47] !== t15 || $[48] !== t17 || $[49] !== t2 || $[50] !== t4 || $[51] !== t5 || $[52] !== t6 || $[53] !== t7 || $[54] !== t9 || $[55] !== value) {
    t18 = <LazyMotion features={domAnimation}><m.div ref={trackRef} role="slider" tabIndex={0} data-slot="elastic-slider-track" data-active={isActive} data-focus-visible={keyboardFocusRing} aria-label={t2} aria-orientation="horizontal" aria-valuemin={min} aria-valuemax={max} aria-valuenow={value} aria-valuetext={displayValue} className={t3} style={t4} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onFocus={handleTrackFocus} onBlur={handleTrackBlur} onKeyDown={handleKeyDown} onMouseEnter={t5} onMouseLeave={t6}>{t7}{t9}{t14}{t15}{t17}</m.div></LazyMotion>;
    $[35] = displayValue;
    $[36] = handleKeyDown;
    $[37] = handlePointerDown;
    $[38] = handlePointerMove;
    $[39] = handlePointerUp;
    $[40] = handleTrackBlur;
    $[41] = handleTrackFocus;
    $[42] = isActive;
    $[43] = keyboardFocusRing;
    $[44] = max;
    $[45] = min;
    $[46] = t14;
    $[47] = t15;
    $[48] = t17;
    $[49] = t2;
    $[50] = t4;
    $[51] = t5;
    $[52] = t6;
    $[53] = t7;
    $[54] = t9;
    $[55] = value;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== t1 || $[58] !== t18) {
    t19 = <div ref={wrapperRef} data-slot="elastic-slider" className={t1}>{t18}</div>;
    $[57] = t1;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  return t19;
}